import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumBackground } from '../components/onboarding/PremiumBackground';
import { DesktopOnboarding } from '../components/onboarding/DesktopOnboarding';
import { OnboardingThemePicker } from '../components/onboarding/OnboardingThemePicker';
import { ONBOARDING_SLIDES } from '../constants/onboarding';
import { useIsDesktop } from '../hooks/useMediaQuery';

const ONBOARDING_KEY = 'cozzysim-onboarding-done';

interface OnboardingPageProps {
  onComplete: () => void;
}

export function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const [slide, setSlide] = useState(0);
  const current = ONBOARDING_SLIDES[slide];
  const isLast = slide === ONBOARDING_SLIDES.length - 1;
  const isContain = current.illustrationFit === 'contain';

  const finish = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    onComplete();
  };

  const next = () => {
    if (isLast) finish();
    else setSlide((s) => s + 1);
  };

  if (isDesktop) {
    return (
      <DesktopOnboarding
        slide={slide}
        total={ONBOARDING_SLIDES.length}
        current={current}
        isLast={isLast}
        onNext={next}
        onSkip={finish}
      />
    );
  }

  return (
    <div className="cs-premium-screen cs-onboarding-premium">
      <PremiumBackground />

      <div className="cs-premium-screen__content cs-onboarding-premium__content">
        <div key={slide} className="cs-onboarding-premium__slide">
          <div className="cs-onboarding-premium__illustration">
            {current.kind === 'theme' ? (
              <OnboardingThemePicker />
            ) : (
              <div
                className={`cs-onboarding-premium__illustration-wrap${
                  isContain ? ' cs-onboarding-premium__illustration-wrap--contain' : ''
                }`}
              >
                <div className="cs-onboarding-premium__illustration-glow" />
                <img
                  src={current.illustration ?? ''}
                  alt=""
                  className="cs-onboarding-premium__image"
                  style={
                    !isContain && current.illustrationScale
                      ? { transform: `scale(${current.illustrationScale})` }
                      : undefined
                  }
                  width={560}
                  height={682}
                  decoding={slide === 0 ? 'sync' : 'async'}
                  loading={slide === 0 ? 'eager' : 'lazy'}
                  draggable={false}
                />
              </div>
            )}
          </div>

          <div className="cs-onboarding-premium__copy">
            <h1 className="cs-onboarding-premium__title">{t(current.titleKey)}</h1>
            <p className="cs-onboarding-premium__subtitle">{t(current.subtitleKey)}</p>
          </div>

          <div className="cs-onboarding-premium__dots" aria-hidden>
            {ONBOARDING_SLIDES.map((_, index) => (
              <span
                key={index}
                className={`cs-onboarding-premium__dot${
                  index === slide ? ' cs-onboarding-premium__dot--active' : ''
                }`}
              />
            ))}
          </div>
        </div>

        <div className="cs-onboarding-premium__footer">
          <button type="button" className="cs-onboarding-premium__btn" onClick={next}>
            {t(current.ctaKey)}
          </button>
        </div>
      </div>
    </div>
  );
}

export function isOnboardingDone(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === '1';
}
